import React, { useState } from "react";
import { motion } from "framer-motion";
import { Gift, Sparkles, Search, Loader2, Trophy } from "lucide-react";
import { useApp } from "../AppContext";
import api from "../api";
import { toast } from "sonner";
import DominicanFlag from "./DominicanFlag";

const LoyaltySection = () => {
    const { t, lang, apiBase } = useApp();
    const [phone, setPhone] = useState("");
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    const lookup = async (e) => {
        e?.preventDefault();
        const digits = phone.replace(/[^\d]/g, "");
        if (digits.length < 7) {
            toast.error(
                lang === "es"
                    ? "Agrega un teléfono válido"
                    : "Add a valid phone number"
            );
            return;
        }
        setLoading(true);
        try {
            const r = await api.get(`${apiBase}/loyalty/${digits}`);
            setData(r.data);
        } catch {
            toast.error(t.common.error);
        } finally {
            setLoading(false);
        }
    };

    const progressPct = data ? ((data.order_count % 10) / 10) * 100 : 0;
    const hasReward = data && data.rewards_earned > 0 && data.order_count % 10 === 0;

    return (
        <section
            id="loyalty"
            data-testid="loyalty-section"
            className="relative py-24 sm:py-32 bg-earth-100 grain overflow-hidden"
        >
            <div className="absolute inset-x-0 top-0 h-1 flex">
                <div className="flex-1" style={{ background: "#002D62" }} />
                <div className="flex-1 bg-white" />
                <div className="flex-1" style={{ background: "#CE1126" }} />
            </div>
            <div
                aria-hidden
                className="absolute -left-32 top-1/2 -translate-y-1/2 w-96 h-96 rounded-full opacity-10 pointer-events-none"
                style={{ background: "radial-gradient(circle, #CE1126 0%, transparent 65%)" }}
            />

            <div className="relative max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="lg:col-span-7"
                >
                    <span className="pill-tag mb-5">
                        <DominicanFlag className="w-5 h-3.5 rounded-sm" />
                        {lang === "es" ? "Programa de Fidelidad" : "Loyalty Program"}
                    </span>
                    <h2 className="font-serif font-black text-earth-900 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                        {lang === "es"
                            ? "Cada 10 pedidos, un plato gratis."
                            : "Every 10 orders, one plate on us."}
                    </h2>
                    <p className="mt-5 text-lg text-earth-700 max-w-lg">
                        {lang === "es"
                            ? "Nuestra forma de decir gracias por ser parte de la familia dominicana. Cuanto más ordenes, más sabor recibes."
                            : "Our way of saying thanks for being part of the Dominican family. The more you order, the more flavor you earn."}
                    </p>

                    <div className="mt-8 grid sm:grid-cols-3 gap-4 max-w-lg">
                        {[
                            { n: "1", label: lang === "es" ? "Ordena con tu teléfono" : "Order with your phone" },
                            { n: "2", label: lang === "es" ? "Acumula 10 pedidos" : "Stack up 10 orders" },
                            { n: "3", label: lang === "es" ? "Recibe un plato gratis" : "Get a free plate" },
                        ].map((s) => (
                            <div key={s.n} className="bg-white border border-earth-200 rounded-2xl p-4">
                                <div className="font-serif font-black text-3xl text-terracotta-500 leading-none">
                                    {s.n}
                                </div>
                                <div className="text-xs text-earth-700 font-semibold mt-1.5 leading-snug">
                                    {s.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className="lg:col-span-5"
                >
                    <div
                        data-testid="loyalty-card-visual"
                        className="relative rounded-[28px] p-7 shadow-2xl overflow-hidden text-white"
                        style={{
                            background:
                                "linear-gradient(135deg, #002D62 0%, #C85A17 55%, #CE1126 100%)",
                        }}
                    >
                        <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full bg-white/10" />
                        <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full bg-white/5" />

                        <div className="relative flex items-center justify-between mb-5">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-80">
                                    Delicias Dominicanas
                                </div>
                                <div className="font-serif font-black text-xl">
                                    {lang === "es" ? "Tarjeta de Fidelidad" : "Loyalty Card"}
                                </div>
                            </div>
                            <Trophy className="w-7 h-7 opacity-90" />
                        </div>

                        <form onSubmit={lookup} className="relative mb-4">
                            <input
                                data-testid="loyalty-phone-input"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder={
                                    lang === "es"
                                        ? "Tu teléfono para consultar"
                                        : "Your phone to look up"
                                }
                                className="w-full bg-white/15 backdrop-blur border border-white/30 rounded-xl px-4 py-3 pr-12 text-white placeholder-white/60 outline-none focus:border-white/70"
                            />
                            <button
                                type="submit"
                                data-testid="loyalty-lookup-btn"
                                disabled={loading}
                                className="absolute right-1.5 top-1.5 bottom-1.5 px-4 rounded-lg bg-white text-earth-900 font-bold text-xs uppercase tracking-wider hover:bg-earth-50 disabled:opacity-50"
                            >
                                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                            </button>
                        </form>

                        {data && (
                            <div data-testid="loyalty-progress" className="relative">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="text-xs uppercase tracking-wider font-bold opacity-80">
                                        {lang === "es" ? "Tu progreso" : "Your progress"}
                                    </div>
                                    <div className="font-serif font-black text-3xl tabular-nums">
                                        {data.order_count % 10}/10
                                    </div>
                                </div>
                                <div className="h-3 bg-white/20 rounded-full overflow-hidden mb-3">
                                    <div
                                        className="h-full bg-white transition-all duration-700 rounded-full"
                                        style={{ width: `${progressPct}%` }}
                                    />
                                </div>
                                {hasReward ? (
                                    <div className="flex items-center gap-2 text-sm">
                                        <Sparkles className="w-4 h-4" />
                                        <span>
                                            {lang === "es" ? "¡Felicidades! Tienes" : "Congrats! You have"}{" "}
                                            <b>{data.rewards_earned}</b>{" "}
                                            {lang === "es" ? "platos gratis acumulados." : "free plates."}
                                        </span>
                                    </div>
                                ) : (
                                    <div className="text-sm opacity-95">
                                        {data.order_count === 0
                                            ? lang === "es"
                                                ? "Aún no tienes pedidos. ¡Ordena hoy para empezar!"
                                                : "No orders yet. Order today to start!"
                                            : lang === "es"
                                            ? `Solo ${data.orders_until_reward} pedidos más para tu próximo plato gratis.`
                                            : `Just ${data.orders_until_reward} more orders for your next free plate.`}
                                    </div>
                                )}
                            </div>
                        )}

                        {!data && (
                            <div className="relative flex items-center gap-3 text-sm bg-white/10 backdrop-blur rounded-xl p-3">
                                <Gift className="w-5 h-5 shrink-0" />
                                <span className="opacity-95">
                                    {lang === "es"
                                        ? "Cada 10 pedidos = 1 plato gratis. ¡Empieza hoy!"
                                        : "Every 10 orders = 1 free plate. Start today!"}
                                </span>
                            </div>
                        )}
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default LoyaltySection;
